import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, ChevronRight, Cog, FileText, Plus, SearchX } from 'lucide-react';

import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { formatIST } from '@/lib/utils';
import { useAssignMachine } from '@/hooks/useAssignMachine';
import { resolveScan } from '@/lib/scanRouting';
import MachinePicker from '@/components/phone/MachinePicker';
import QrScanner from '@/components/phone/QrScanner';
import {
  AppHeader, EmptyState, SearchBar, ScreenLoader, Spinner, StatusPill, TabRow,
} from '@/components/phone/primitives';
import { Colors, greetingKey } from '@/components/phone/theme';

/**
 * One screen, one list: every job card this supervisor is responsible for —
 * what is standing at his stages now, and what is routed through them later.
 *
 * The web board used to be a dashboard of company-wide KPI tiles, four status
 * tabs and three buttons into separate queue/order/machine pages — four views of
 * the same batches, counted four different ways, none of them scoped to the
 * supervisor's own stages. This is the phone's board, screen for screen, so a
 * supervisor on iOS is shown the same thing as the one on Android standing
 * beside him.
 *
 * Two tabs, and they answer the only question the board has: is this mine, or is
 * it the factory's. My Job Cards is every job whose pipeline runs through one of
 * his stages; All Job Cards is everything in production.
 *
 * Every row he runs is finished the same way: one press on Done, and the toast
 * is the receipt. Work that has not formally arrived is finished by the same
 * press, because the floor runs ahead of the paperwork.
 *
 * Putting a machine on a batch is the thing a supervisor does most, so it
 * happens here, from the row. Everything else lives on the card.
 *
 * Mirrors `mobile-frontend/src/screens/home/SupervisorHome.tsx`.
 */

const SCOPES = [
  { key: 'mine', labelKey: 'supervisor.my_job_cards', testID: 'scope-tab-mine' },
  { key: 'all', labelKey: 'supervisor.all_job_cards', testID: 'scope-tab-all' },
];

export default function SupervisorBoardPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [jobCards, setJobCards] = useState([]);
  const [myStages, setMyStages] = useState([]);
  const [scope, setScope] = useState('mine');
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);
  const [scanning, setScanning] = useState(false);
  const [scanBlock, setScanBlock] = useState(null);
  const [completing, setCompleting] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const params = new URLSearchParams({ tab: 'board', scope, page: '1', limit: '200' });
      if (search) params.set('search', search);
      const [board, unread] = await Promise.all([
        api.get(`/supervisor/job-cards?${params.toString()}`),
        api.get('/notifications/unread-count'),
      ]);
      setJobCards(board.data.job_cards || []);
      setTotal(board.data.total ?? (board.data.job_cards || []).length);
      setMyStages(board.data.my_stages || []);
      setUnreadCount(unread.data.count || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load the board');
    }
    setLoading(false);
  }, [scope, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const assignment = useAssignMachine((outcome) => {
    if (outcome.ok) { toast.success(outcome.message); fetchData(); } else toast.error(outcome.message);
  });

  /**
   * Finishing a stage is a yes/no on the floor: the whole batch is off the
   * machine or it is not. One press passes the full quantity on — a confirm
   * dialog on the day's most repeated action is a tap that never buys anything.
   * A part-finished batch is a different decision and still lives on the job
   * card, where the quantity can be typed.
   *
   * The stage sent is the one the row is about — his stage, whether the job is
   * standing at it or still coming to it.
   */
  const completeStage = async (card) => {
    if (completing) return;
    setCompleting(card.row_key);
    try {
      const res = await api.post(`/supervisor/batches/${card.batch_id}/pass-to-next-stage`, {
        qty_to_pass: card.quantity || 0,
        stage_name: card.board_stage || card.current_stage_name || undefined,
      });
      toast.success(res.data?.message || t('supervisor.stage_done'));
      fetchData();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || t('common.error'));
    }
    setCompleting('');
  };

  const assignFrom = (card) => assignment.start({
    batchId: card.batch_id,
    stageName: card.current_stage_name || '',
    label: `${card.item_name || 'Job'} · ${card.quantity ?? 0} pcs`,
  });

  // the same resolver the Scan tab uses: a printed code says what it is
  const handleScan = async (data) => {
    setScanning(false);
    const outcome = await resolveScan(data, navigate, t);
    if (!outcome.ok) setScanBlock(outcome);
  };

  // GET /job-cards/{id} resolves a job card id or a line item id, nothing else
  const openJobCard = (card) =>
    navigate(`/supervisor/job-card/${card.job_card_id || card.id || card.line_item_id}`);

  // a supervisor with no linked stages runs the whole factory, so every row on
  // the wide board is still his to act on
  const runsEverything = myStages.length === 0;
  const isMine = useCallback(
    (card) => runsEverything || myStages.includes(card.board_stage || card.current_stage_name || ''),
    [runsEverything, myStages],
  );

  // two different things to know: what is on the floor, and what is heading for
  // it. One number for both would hide whichever the supervisor was after.
  //
  // The factory can run past the 200 rows a page carries, and a split tallied
  // off a truncated page would read as the whole plant — so once the server says
  // there is more, the header says how much of it is here instead.
  const countText = useMemo(() => {
    if (total > jobCards.length) return `${jobCards.length} / ${total}`;
    const coming = jobCards.filter((c) => c.board_state === 'upcoming').length;
    const onFloor = jobCards.length - coming;
    return coming
      ? `${onFloor} ${t('supervisor.on_floor')} · ${coming} ${t('supervisor.coming')}`
      : `${onFloor} ${onFloor === 1 ? 'job card' : 'job cards'}`;
  }, [jobCards, total, t]);

  return (
    <div className="flex flex-1 flex-col px-5">
      <AppHeader
        greeting={t(`dashboard.${greetingKey()}`)}
        subtitle={user?.full_name || ''}
        unreadCount={unreadCount}
        onNotifications={() => navigate('/supervisor/notifications')}
        onScan={() => { setScanBlock(null); setScanning(true); }}
        testID="notifications-btn"
      />

      {/* the tab is the title: two of them, and which one is on says what the
          list underneath is */}
      <TabRow
        tabs={SCOPES.map((s) => ({ key: s.key, label: t(s.labelKey), testID: s.testID }))}
        active={scope}
        onSelect={setScope}
      />

      {/* the tab above says whose work this is; naming his dozen stages under it
          only cost two lines and told him nothing */}
      <p className="mt-2 text-right text-[11px] font-medium" style={{ color: Colors.text.tertiary }}>
        {countText}
      </p>

      <div className="mt-3">
        <SearchBar
          testID="board-search"
          placeholder={t('supervisor.search_placeholder')}
          onChange={setSearch}
        />
      </div>

      {!!scanBlock && (
        <div
          data-testid="scan-blocked"
          className="mt-3 rounded-[14px] border p-4 text-center"
          style={{ backgroundColor: Colors.semantic.cautionWash, borderColor: '#E8DCC4' }}
        >
          <p className="text-[15px] font-bold" style={{ color: Colors.semantic.caution }}>{scanBlock.title}</p>
          <p className="mt-1 text-[13px] leading-5" style={{ color: Colors.semantic.caution }}>{scanBlock.message}</p>
          <button
            type="button"
            onClick={() => setScanBlock(null)}
            className="mt-3 rounded-[10px] border px-5 py-2 text-[13px] font-bold"
            style={{ backgroundColor: Colors.background.primary, borderColor: '#E8DCC4', color: Colors.semantic.caution }}
          >
            {t('scan.scan_again')}
          </button>
        </div>
      )}

      {loading ? <ScreenLoader /> : jobCards.length === 0 ? (
        <EmptyState
          icon={search ? SearchX : FileText}
          message={search
            ? t('supervisor.no_matches')
            : t(scope === 'all' ? 'supervisor.no_job_cards_all' : 'supervisor.no_job_cards')}
          // packing and loading left this board on purpose — say where to
          hint={search ? undefined : t('supervisor.packing_is_in_dispatch')}
        />
      ) : (
        <ul className="pb-6">
          {jobCards.map((card) => (
            <BoardRow
              key={card.row_key}
              card={card}
              t={t}
              mine={isMine(card)}
              completing={completing}
              assigning={assignment.assigning}
              onOpen={() => openJobCard(card)}
              onAssign={() => assignFrom(card)}
              onComplete={() => completeStage(card)}
            />
          ))}
        </ul>
      )}

      <QrScanner open={scanning} onScan={handleScan} onClose={() => setScanning(false)} />

      <MachinePicker
        open={!!assignment.target}
        onClose={assignment.cancel}
        stageName={assignment.target?.stageName || ''}
        subtitle={assignment.target?.label}
        machines={assignment.machines}
        loadingMachines={assignment.loading}
        assigning={assignment.assigning}
        onAssign={assignment.pick}
      />
    </div>
  );
}

function BoardRow({ card, t, mine, completing, assigning, onOpen, onAssign, onComplete }) {
  const stock = card.order_kind === 'stock';
  // a job whose plate is not back cannot start, whatever machine it is put on —
  // saying so here is what stops the press being blocked
  const platePending = !!card.plate_required && card.plate_status !== 'received';
  // not on his floor yet: it sits quieter than the work that is
  const upcoming = card.board_state === 'upcoming';
  // a machine can only be put on work that has arrived; finishing the stage
  // cannot wait for that, because the floor runs ahead of the paperwork
  const canAssign = !upcoming && mine;
  const canComplete = mine && card.status !== 'completed';

  return (
    <li
      data-testid={`job-card-${card.row_key}`}
      className={`mt-3 flex items-center gap-2 rounded-[10px] border px-4 py-3 ${upcoming ? 'border-dashed' : ''}`}
      style={{
        backgroundColor: upcoming ? Colors.background.secondary : Colors.background.primary,
        borderColor: Colors.border.default,
      }}
    >
      <div className="min-w-0 flex-1">
        {/* what it is, then who it is for. A supervisor is looking for the job,
            not the customer — the floor knows a card by what is being made on
            it. A stock run is for us, so that is what stands where the customer
            would. */}
        <button type="button" onClick={onOpen} className="block w-full text-left">
          <span className="line-clamp-2 block text-[15px] font-bold -tracking-[0.2px]" style={{ color: Colors.text.primary }}>
            {card.item_name || 'Untitled item'}
          </span>
          <span className="mt-0.5 block truncate text-[13px] font-medium" style={{ color: Colors.text.secondary }}>
            {stock ? t('supervisor.internal_stock') : (card.firm_name || card.customer_name || 'Unknown')}
          </span>
          <span className="mt-0.5 block truncate text-[11px]" style={{ color: Colors.text.tertiary }}>
            {card.quantity} pcs · {card.order_id}
            {card.delivery_date ? ` · ${formatIST(card.delivery_date, { compact: true })}` : ''}
          </span>
        </button>

        <div className="mt-2 flex flex-wrap items-center gap-2">
          {/* what kind of run it is, then where it stands */}
          <StatusPill
            label={stock ? t('supervisor.stock') : t('supervisor.order')}
            tone={stock ? 'caution' : 'accent'}
          />
          <StatusPill
            label={(upcoming && card.in_design
              ? t('supervisor.in_design')
              : card.current_stage_name) || t('supervisor.unassigned')}
            tone="neutral"
            dot={!upcoming}
          />
          {/* where it is coming to, so the row says what it is doing on this
              board at all */}
          {upcoming && !!card.board_stage && (
            <StatusPill testID={`coming-to-${card.row_key}`} label={`→ ${card.board_stage}`} tone="neutral" />
          )}
          {/* the machine is the one thing a supervisor changes from here, so the
              pill that shows it is the button that sets it */}
          {canAssign && (
            <button
              type="button"
              data-testid={`assign-${card.row_key}`}
              onClick={onAssign}
              disabled={assigning}
              className={`flex min-w-0 shrink items-center gap-1 rounded-md border px-2 py-[5px] disabled:opacity-60 ${
                card.machine_assigned ? '' : 'border-dashed'
              }`}
              style={{ borderColor: card.machine_assigned ? Colors.border.default : Colors.accent.DEFAULT }}
            >
              {card.machine_assigned
                ? <Cog className="h-3 w-3 shrink-0" style={{ color: Colors.text.secondary }} />
                : <Plus className="h-3 w-3 shrink-0" style={{ color: Colors.accent.DEFAULT }} />}
              <span
                className="truncate text-[11px]"
                style={{
                  color: card.machine_assigned ? Colors.text.secondary : Colors.accent.DEFAULT,
                  fontWeight: card.machine_assigned ? 500 : 700,
                }}
              >
                {card.machine_assigned ? card.machine_name : t('supervisor.no_machine')}
              </span>
            </button>
          )}
          {platePending && <StatusPill label={t('supervisor.plate_pending')} tone="caution" />}
          {(card.total_batches ?? 0) > 1 && (
            <span className="text-[10px] font-medium" style={{ color: Colors.accent.DEFAULT }}>
              B{card.batch_number}/{card.total_batches}
            </span>
          )}
          {card.priority === 'high' && <StatusPill label="Urgent" tone="critical" />}
        </div>
      </div>

      {!canComplete ? (
        <ChevronRight className="h-4 w-4 shrink-0" style={{ color: Colors.text.tertiary }} />
      ) : (
        /* the row's second action, and deliberately the quieter one — an
           outline, not a filled button, so finishing a stage never looks like
           the point of the list */
        <button
          type="button"
          data-testid={`complete-${card.row_key}`}
          onClick={onComplete}
          disabled={!!completing}
          className="flex min-w-[62px] shrink-0 items-center justify-center gap-1 rounded-md border px-2 py-1.5 disabled:opacity-60"
          style={{ borderColor: Colors.border.default }}
        >
          {completing === card.row_key ? <Spinner size="sm" /> : (
            <>
              <Check className="h-3.5 w-3.5" style={{ color: Colors.status.success }} />
              <span className="text-[11px] font-semibold" style={{ color: Colors.status.success }}>
                {t('supervisor.complete')}
              </span>
            </>
          )}
        </button>
      )}
    </li>
  );
}
